import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/Toast';
import './Wishlist.css';

function Wishlist() {
  const navigate = useNavigate();
  const { toast, showToast, closeToast } = useToast();
  const [user, setUser] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);

    // Check if user is a customer
    if (parsedUser.role !== 'customer') {
      navigate('/admin/dashboard');
      return;
    }

    setUser(parsedUser);
    fetchWishlist(parsedUser.id);
  }, [navigate]);

  const fetchWishlist = async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get('/api/auth/wishlist', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlistItems(response.data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`/api/auth/wishlist/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove from local state
      setWishlistItems(wishlistItems.filter(item => item.product_id !== productId));
      showToast('Item removed from wishlist', 'success');
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      showToast('Failed to remove item from wishlist', 'error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="wishlist">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="wishlist">
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      <div className="account-container">
        <div className="account-sidebar">
          <div className="account-user-info">
            <div className="user-avatar">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <h3>{user?.name || 'Customer'}</h3>
            <p>{user?.email}</p>
          </div>

          <nav className="account-nav">
            <Link to="/account" className="account-nav-item">
              Dashboard
            </Link>
            <Link to="/account/orders" className="account-nav-item">
              Order History
            </Link>
            <Link to="/account/addresses" className="account-nav-item">
              Addresses
            </Link>
            <Link to="/account/wishlist" className="account-nav-item active">
              Wishlist
            </Link>
            <Link to="/account/settings" className="account-nav-item">
              Settings
            </Link>
            <button onClick={handleLogout} className="account-nav-item logout-btn">
              Logout
            </button>
          </nav>
        </div>

        <div className="account-main">
          <div className="account-header">
            <h1>My Wishlist</h1>
            <p>Save your favorite items for later</p>
          </div>

          {wishlistItems.length === 0 ? (
            <div className="empty-state">
              <h3>Your Wishlist is Empty</h3>
              <p>Start adding products you love to your wishlist</p>
              <Link to="/" className="btn btn-primary">Browse Products</Link>
            </div>
          ) : (
            <div className="wishlist-grid">
              {wishlistItems.map((item) => (
                <div key={item.product_id} className="wishlist-item">
                  <Link to={`/product/${item.product_slug}`} className="item-image-link">
                    {item.product_image ? (
                      <img src={item.product_image} alt={item.product_name} className="item-image" />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </Link>

                  <div className="item-details">
                    <Link to={`/product/${item.product_slug}`} className="item-name">
                      <h3>{item.product_name}</h3>
                    </Link>

                    {item.product_description && (
                      <p className="item-description">
                        {item.product_description.substring(0, 100)}
                        {item.product_description.length > 100 ? '...' : ''}
                      </p>
                    )}

                    <div className="item-price">
                      ${parseFloat(item.product_price).toFixed(2)}
                    </div>

                    <div className="item-added">
                      Added {new Date(item.added_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                  </div>

                  <div className="item-actions">
                    <Link
                      to={`/product/${item.product_slug}`}
                      className="btn btn-primary btn-sm"
                    >
                      View Product
                    </Link>
                    <button
                      onClick={() => handleRemoveFromWishlist(item.product_id)}
                      className="btn btn-secondary-outline btn-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {wishlistItems.length > 0 && (
            <div className="wishlist-summary">
              <p>{wishlistItems.length} item{wishlistItems.length !== 1 ? 's' : ''} in your wishlist</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Wishlist;
